import { useEffect, useState } from "react";

import { Button, Callout, Colors, Intent } from "@blueprintjs/core";
import { Link } from "react-router";

import { Box } from "@/components/layout/flexbox";
import {
  UserGroupRow,
  UserListStyled,
} from "@/components/pages_logged_in/workspace-admin/SetGroupForUsersDialog";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { useSetWorkspaceMemberCreditsMutation } from "@/graphql";
import { StatsMember } from "@/types/workspaces";
import { formatCredits } from "@/utils/string";

import { SetCreditsForm } from "./SetCreditsForm";

interface ResultsProps {
  result: Result;
  onClose: () => void;
}

interface Result {
  success?: boolean | null;
  successNumber?: number | null;
  total?: number | null;
}

function Results({ result, onClose }: ResultsProps) {
  const resultView = result?.success ? (
    <Callout intent="success" icon="tick">
      Credits successfully set
    </Callout>
  ) : (
    <Callout intent="warning">
      Your workspace does not have enough credits. You can{" "}
      <Link to="/buy">buy more credits</Link>
      <Box>
        <b style={{ color: Colors.GREEN3 }}>
          Update successful: {result.successNumber}
        </b>
        ,{" "}
        <b style={{ color: Colors.RED3 }}>
          Update failed:{" "}
          {result.total ? result.total - (result.successNumber || 0) : 0}
        </b>
      </Box>
    </Callout>
  );
  return (
    <Box>
      {resultView}
      <Box mt={16}>
        <Button
          intent="primary"
          large={true}
          onClick={() => onClose()}
          loading={false}
        >
          Close
        </Button>
      </Box>
    </Box>
  );
}

interface ModifyCreditsDialogProps {
  members: StatsMember[];
  selectAllUsers?: boolean;
  excludedUsers?: string[];
  totalRecords?: number;
  open: boolean;
  workspaceCredit?: string | null;
  afterCreditSet: () => void;
  onClose: () => void;
}

export function ModifyCreditsDialog({
  members,
  selectAllUsers = false,
  excludedUsers = [],
  totalRecords = 0,
  open,
  workspaceCredit,
  afterCreditSet,
  onClose,
}: ModifyCreditsDialogProps) {
  const [result, setResult] = useState<Result>();

  const creditsText = formatCredits(workspaceCredit);
  const [first] = members;

  const availableCredits = first?.availableCredits || "0";
  const [amount, setAmount] = useState(availableCredits);

  // Calculate the actual number of users that will be affected
  const affectedUsersCount = selectAllUsers
    ? totalRecords - excludedUsers.length
    : members.length;

  useEffect(() => {
    if (first) {
      setAmount(availableCredits);
    }
  }, [first]);

  const [transferMemberCredits, { loading: transferingToMember }] =
    useSetWorkspaceMemberCreditsMutation({
      onCompleted: (data) => {
        const { setWorkspaceMemberCredits } = data;
        const {
          success: successNumber,
          ok,
          total,
        } = setWorkspaceMemberCredits || {};
        setResult({ success: ok, successNumber: successNumber, total: total });
        afterCreditSet();
      },
      onError: () => {
        setResult({ success: false, successNumber: 0, total: 0 });
      },
    });

  const loading = transferingToMember;

  function transfer() {
    const clientIds = members.map((i) => i.user.id);

    transferMemberCredits({
      variables: {
        clientIds: clientIds,
        amount: amount,
      },
    });
  }

  return (
    <StyledDialog
      isOpen={open}
      onClose={() => {
        onClose();
      }}
      onOpening={() => {
        setResult(undefined);
      }}
      lazy={true}
      title="Modify Credits"
      width="480px"
    >
      <Box p={24}>
        {result ? (
          <Results result={result} onClose={onClose} />
        ) : (
          <Box>
            <Box mb={3}>
              <Callout intent={Intent.PRIMARY} icon={null}>
                <Box fontSize={14}>
                  <>
                    As a workspace admin, you can move credits between your
                    workspace and{" "}
                    {selectAllUsers
                      ? `all ${affectedUsersCount} selected members`
                      : affectedUsersCount > 1
                        ? "these members"
                        : "this member"}
                    .
                    {selectAllUsers && excludedUsers.length > 0 && (
                      <> ({excludedUsers.length} users excluded)</>
                    )}
                  </>
                </Box>
              </Callout>
            </Box>

            <UserListStyled>
              {members.map((member) => {
                return (
                  <UserGroupRow
                    key={`member-remove-${member.id}`}
                    member={member}
                    isShowGroup={false}
                    isShowCredits={true}
                  />
                );
              })}
            </UserListStyled>
            <Box my={3}>
              <Box py={2} fontWeight={600}>
                Set Credits
              </Box>
              <SetCreditsForm
                workspaceCredit={workspaceCredit}
                creditsText={creditsText}
                amount={amount}
                userCredit={availableCredits}
                setAmount={setAmount}
                numberOfEmails={affectedUsersCount}
              />
            </Box>
            <Button
              intent="primary"
              large={true}
              disabled={loading}
              onClick={transfer}
              loading={loading}
            >
              Confirm
            </Button>
          </Box>
        )}
      </Box>
    </StyledDialog>
  );
}
